// PosInvoice.js
// SAME VERSION — only slightly increased text sizes (no other logic/design changes)

function PosInvoice(order, orderItems = []) {
  if (!order) return;

  const items = Array.isArray(orderItems) ? orderItems : [];

  const subtotal = Number(order.orderSubtotal ?? 0);
  const discount = Number(order.promoDiscountAmount ?? 0);
  const grandTotal =
    order.orderTotal != null ? Number(order.orderTotal) : subtotal - discount;

  const createdAt = order.orderAddedDate
    ? new Date(order.orderAddedDate)
    : new Date();

  // try to show staff name if saved in posMeta
  const staffName =
    order?.posMeta?.staffId ||
    order?.posMeta?.staffName ||
    "";

  // transaction number (card treated as SSL)
  const txn =
    (order?.sslTransactionNumber || order?.bkashTransactionNumber || "").trim();

  const paymentLabel =
    order.paymentMethod === "cash"
      ? "Cash"
      : order.paymentMethod === "card"
      ? "Card (SSL)"
      : order.paymentMethod === "bkash"
      ? "bKash"
      : (order.paymentMethod || "");

  // IMPORTANT: Thermal printers often print "৳" as "?"
  // Use "Tk" for perfect compatibility.
  const CURRENCY_LABEL = "Tk";

  const money = (n) => {
    const num = Number(n || 0);
    return Number.isFinite(num) ? num.toString() : "0";
  };

  // --------- helpers inside the print window ----------
  function escapeHtml(str) {
    return String(str || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  // Receipt-style line items (thermal-friendly)
  const itemsHTML =
    items.length > 0
      ? items
          .map((i) => {
            const name = (i?.item?.name || "").toString();
            const size = (i?.size?.name || "").toString();
            const qty = Number(i?.quantity || 0);
            const unitPrice = money(i?.unitPrice);
            const totalPrice = money(i?.totalPrice);

            return `
              <div class="item">
                <div class="item-top">
                  <div class="name">${escapeHtml(name)}</div>
                  <div class="price">${totalPrice} ${CURRENCY_LABEL}</div>
                </div>
                <div class="item-sub">
                  <div class="meta">Size: ${escapeHtml(size || "-")}</div>
                  <div class="meta right">Qty: ${qty} × ${unitPrice} ${CURRENCY_LABEL}</div>
                </div>
              </div>
            `;
          })
          .join("")
      : `<div class="empty">No line items found</div>`;

  const win = window.open("", "_blank", "width=380,height=800");
  if (!win) {
    alert("Popup blocked. Please allow popups to print the invoice.");
    return;
  }

  const invoiceNo = escapeHtml(order.invoiceNo || "");
  const customerName = escapeHtml(order.orderCustomerName || "");
  const customerPhone = escapeHtml(order.orderCustomerPhone || "");
  const customerEmail = escapeHtml(order.orderCustomerEmail || "");
  const instructions = escapeHtml(order.additionalInstructions || "");
  const dateStr = createdAt.toLocaleString();

  win.document.open();
  win.document.write(`
    <html>
      <head>
        <title>Receipt ${invoiceNo}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>
  /* ---------- Print page size ---------- */
  @page {
    size: 80mm auto;
    margin: 5mm; /* balanced between 4 and 6 to avoid browser scaling */
  }

  /* ---------- Thermal-safe printing ---------- */
  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ---------- Base reset ---------- */
  html, body {
    height: auto;
    margin: 0;
    padding: 0;
    background: #fff;
  }

  body {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      "Liberation Mono", "Courier New", monospace;

    font-size: 13.5px;      /* was 13 */
    line-height: 1.26;
    color: #000;
    font-weight: 400;

    /* IMPORTANT: keep rendering normal (removes uneven bold/light issue) */
    /* removed font-smoothing and geometricPrecision */
  }

  .receipt {
    width: 80mm;
  }

  .center { text-align: center; }

  /* ---------- Header ---------- */
  .title {
    font-size: 22.5px;   /* was 22 */
    font-weight: 700;    /* was 900 → caused bulky print */
    letter-spacing: 0.2px;
  }

  .sub {
    font-size: 11.5px;   /* was 11 */
    margin-top: 2px;
    font-weight: 400;
    color: #000;
  }

  .hr {
    border-top: 1px dashed #000;
    margin: 7px 0;
  }

  /* ---------- Rows ---------- */
  .row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    margin: 2px 0;
  }
  .row .left { flex: 1; min-width: 0; }
  .row .right { text-align: right; white-space: nowrap; }

  .small { font-size: 12.5px; }  /* was 12 */
  .strong { font-weight: 600; }  /* softer than 700 */

  /* ---------- Items ---------- */
  .items { margin-top: 2px; }

  .item {
    padding: 5px 0;
    border-bottom: 1px dotted #000;
  }
  .item:last-child { border-bottom: none; }

  .item-top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }

  .name {
    font-weight: 600;          /* reduced from 700 */
    flex: 1;
    min-width: 0;
    max-width: 52mm;
    word-break: break-word;
    font-size: 13.5px;         /* was 13 */
  }

  .price {
    font-weight: 600;          /* reduced from 700 */
    white-space: nowrap;
    min-width: 22mm;
    text-align: right;
    font-size: 13.5px;
  }

  .item-sub {
    margin-top: 2px;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }

  .meta {
    font-size: 11.5px;   /* was 11 */
    font-weight: 400;
    color: #000;
  }

  .empty {
    text-align: center;
    padding: 10px 0;
  }

  /* ---------- Totals ---------- */
  .totals .row { margin: 3px 0; }

  .grand {
    font-size: 14.5px;   /* was 14 */
    font-weight: 700;    /* was 900 */
    border-top: 1px solid #000;
    padding-top: 5px;
    margin-top: 5px;
  }

  /* ---------- Footer ---------- */
  .footer {
    margin-top: 8px;
  }

  @media print {
    html, body { width: 80mm; }
  }
</style>

      </head>

      <body>
        <div class="receipt">
          <div class="center">
            <div class="title">STRIDES CO</div>
            <div class="sub">POS SALES RECEIPT</div>
            <div class="sub">BIN NUMBER: <span class="strong">0034293920101</span></div>
          </div>

          <div class="hr"></div>

          <div class="row small">
            <div class="left"><span class="strong">Invoice:</span> ${invoiceNo}</div>
            <div class="right">${escapeHtml(dateStr)}</div>
          </div>

          <div class="row small">
            <div class="left"><span class="strong">Customer:</span> ${customerName || "-"}</div>
          </div>

          ${customerPhone ? `
            <div class="row small">
              <div class="left"><span class="strong">Mobile:</span> ${customerPhone}</div>
            </div>
          ` : ""}

          ${customerEmail ? `
            <div class="row small">
              <div class="left"><span class="strong">Email:</span> ${customerEmail}</div>
            </div>
          ` : ""}

          <div class="row small">
            <div class="left"><span class="strong">Payment:</span> ${escapeHtml(paymentLabel)}</div>
          </div>

          ${txn ? `
            <div class="row small">
              <div class="left"><span class="strong">Txn:</span> ${escapeHtml(txn)}</div>
            </div>
          ` : ""}

          ${staffName ? `
            <div class="row small">
              <div class="left"><span class="strong">Prepared By:</span> ${escapeHtml(staffName)}</div>
            </div>
          ` : ""}

          ${instructions ? `
            <div class="hr"></div>
            <div class="small">
              <div class="strong">Instructions:</div>
              <div>${instructions}</div>
            </div>
          ` : ""}

          <div class="hr"></div>

          <div class="row small strong">
            <div class="left">ITEMS</div>
            <div class="right">AMOUNT</div>
          </div>

          <div class="items">
            ${itemsHTML}
          </div>

          <div class="hr"></div>

          <div class="totals">
            <div class="row">
              <div class="left">Subtotal</div>
              <div class="right">${money(subtotal)} ${CURRENCY_LABEL}</div>
            </div>
            <div class="row">
              <div class="left">Discount</div>
              <div class="right">- ${money(discount)} ${CURRENCY_LABEL}</div>
            </div>
            <div class="row grand">
              <div class="left">GRAND TOTAL</div>
              <div class="right">${money(grandTotal)} ${CURRENCY_LABEL}</div>
            </div>
          </div>

          <div class="hr"></div>

          <div class="footer center small">
            <div class="strong">Thank you for shopping with STRIDES CO</div>
            <div>Exchange/Return as per policy.</div>
            <div>www.stridesco.com</div>
          </div>
        </div>

        <script>
          window.onload = function () {
            setTimeout(function () {
              window.print();
            }, 50);

            window.onafterprint = function () {
              window.close();
            };
          };
        </script>
      </body>
    </html>
  `);
  win.document.close();
}

export default PosInvoice;
